import { useState } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import EmailToolbar from '@/components/email/EmailToolbar';
import EmailComposeOverlay from '@/components/email/layout/EmailComposeOverlay';
import EmailMobileNavigation from '@/components/email/layout/EmailMobileNavigation';
import PageTransition from '@/components/layout/PageTransition';
import EmailFolderView from '@/pages/email/EmailFolderView';
import EmailDetailView from '@/pages/email/EmailDetailView';
import DatabaseErrorAlert from '@/components/email/DatabaseErrorAlert';
import { useEmailDatabaseError } from '@/hooks/email/useEmailDatabaseError';
import { useIsMobile } from '@/hooks/use-mobile';
import { useComposeEmail } from '@/hooks/email/useComposeEmail';

// Component to handle draft email routing
const DraftEmailHandler = () => {
  const { emailId } = useParams<{ emailId: string }>();
  const navigate = useNavigate();
  
  const handleClose = () => {
    // Navigate back to drafts folder after closing
    navigate('/app/email/drafts');
  };

  const handleSent = () => {
    // Navigate to sent folder after sending
    navigate('/app/email/sent');
  };

  return (
    <>
      {/* Keep the folder view visible */}
      <EmailFolderView />
      {/* Overlay the compose dialog */}
      <EmailComposeOverlay 
        showCompose={true}
        onClose={handleClose}
        draftId={emailId}
        onSent={handleSent}
      />
    </>
  );
};

// Component to handle email detail routing
const EmailDetailHandler = () => {
  const { folder, emailId } = useParams<{ folder: string; emailId: string }>();
  
  // If it's a draft email, render the draft handler instead
  if (folder === 'drafts') {
    return <DraftEmailHandler />;
  }
  
  // Otherwise render the normal email detail view
  return <EmailDetailView />;
};

const EmailLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { databaseError, clearError } = useEmailDatabaseError();
  const isMobile = useIsMobile();
  const { openCompose } = useComposeEmail();

  const handleRetryConnection = () => {
    clearError();
    window.location.reload();
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Always show EmailToolbar */}
      <EmailToolbar
        onComposeClick={() => openCompose()}
        onMenuClick={() => setSidebarOpen(true)}
        usingMockData={databaseError}
      />

      {databaseError && <DatabaseErrorAlert onRetry={handleRetryConnection} />}

      {isMobile && (
        <EmailMobileNavigation
          isOpen={sidebarOpen}
          onOpenChange={setSidebarOpen}
          onCompose={() => openCompose()}
        />
      )}

      <div className="flex-1 overflow-auto">
        <PageTransition>
          <Routes>
            {/* Default redirect to inbox */}
            <Route path="/" element={<Navigate to="inbox" replace />} />
            {/* Email detail view - includes draft handling */}
            <Route path=":folder/:emailId" element={<EmailDetailHandler />} />
            {/* Email folder views */}
            <Route path=":folder" element={<EmailFolderView />} />
          </Routes>
        </PageTransition>
      </div>

      {/* New-message compose is global (GlobalComposeOverlay in AppLayout);
          the route-based draft editor below stays for /app/email/drafts/:id */}
    </div>
  );
};

export default EmailLayout;
